import { useState, type ReactNode } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Body copy — what the user is confirming. */
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions. */
  danger?: boolean;
  /** Async confirm handler; the dialog shows a spinner and closes on success. */
  onConfirm: () => void | Promise<void>;
}

/** Drop-in replacement for `window.confirm` — a proper, animated modal. */
export function ConfirmDialog({
  open,
  onClose,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger,
  onConfirm,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={title}
      size="sm"
      staticBehavior={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={run} disabled={busy} shine={!danger}>
            {busy ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      {body && <p className="text-[13.5px] leading-relaxed text-ink-600">{body}</p>}
    </Modal>
  );
}
